import * as React from 'react';
import { compose, setDisplayName, withHandlers, withState, withProps, lifecycle } from 'recompose';
import { translate } from 'react-i18next';
import { TranslateProps } from 'util/i18n';
import { ReportSettings, ReportSettingsChangeHandler } from 'logic/reports/ReportSettings';
import { Fade } from 'ui/transitions';
import { makeSentenceFragments } from 'util/makeSentenceFragments';
import { Input } from 'ui/shared/Input';

export type OwnProps = {
    reportSettings: Readonly<ReportSettings>;
    onChange: ReportSettingsChangeHandler;
};

// Helper function to render fragments of the translated sentence
// i.e. "Every <INTERVAL/> days"
// This allows the input fields to be in different locations
// (relative to the sentence fragments) for different languages
const renderFragment = ({
    enabled,
    frequency,
    handleChangeFrequency,
    t,
}: RecomposeProps & TranslateProps) => (fragment: string, i: number) => {
    switch (fragment) {
        case '<INTERVAL/>':
            return (
                <Input
                    key={ i }
                    type="number"
                    id="edit-report-daily-days"
                    value={ frequency }
                    onChange={ handleChangeFrequency }
                    min={ 1 }
                    max={ 99 }
                    maxLength={ 2 }
                    disabled={ !enabled }
                />
            );
        default:
            return (
                <span key={ i }>
                    { fragment }
                </span>
            );
    }
};

const RawRecurrenceDaily = (props: OwnProps & RecomposeProps & TranslateProps) =>
    <Fade
        id="edit-schedule-recurrence-panel-daily"
        className="edit-schedule-recurrence-panel"
        visible={ props.enabled }
        duration={ 400 }
    >
        <div>
            <label>
                <input
                    type="radio"
                    id="edit-report-daily-every-day"
                    value="EveryDay"
                    checked={ !props.weekDays }
                    onChange={ props.handleChangeWeekDays }
                    disabled={ !props.enabled }
                />
                { props.sentenceFragments.map(
                    renderFragment(props)
                ) }
            </label>
            <br/>
            <label>
                <input
                    type="radio"
                    id="edit-report-daily-weekdays"
                    value="WeekDays"
                    checked={ props.weekDays }
                    onChange={ props.handleChangeWeekDays }
                    disabled={ !props.enabled }
                />
                { props.t('Every_weekday') }
            </label>
        </div>
    </Fade>
;

type RecomposeProps = {
    enabled: boolean;
    sentenceFragments: string[];
    weekDays: boolean;
    frequency: string;
    handleChangeFrequency: React.ChangeEventHandler<HTMLInputElement>;
    handleChangeWeekDays: React.ChangeEventHandler<HTMLInputElement>;
};
type _RecomposeProps = RecomposeProps & {
    _setFrequency: (f: string) => void;
    _setWeekDays: (b: boolean) => void;
};

export const RecurrenceDaily = compose<OwnProps & RecomposeProps & TranslateProps, OwnProps>(
    setDisplayName('RunOnScheduleRecurrenceDaily'),
    translate(),
    withProps(({ reportSettings }: OwnProps) => ({
        enabled: reportSettings.scheduleType === 'Daily',
    })),
    withState('weekDays', '_setWeekDays', ({ reportSettings }: OwnProps) => reportSettings.scheduleWeekdays),
    withState('frequency', '_setFrequency', ({ reportSettings }: OwnProps) =>
        reportSettings.scheduleType === 'Daily'
            ? reportSettings.scheduleFrequency
            : 1,
    ),
    withHandlers({
        handleChangeFrequency: ({ onChange, _setFrequency }: OwnProps & _RecomposeProps) => (e: React.ChangeEvent<HTMLInputElement>) => {
            _setFrequency(e.target.value);
            const frequency = parseInt(e.target.value, 10);
            onChange(reportSettings => reportSettings.setScheduleWeekdays(false, frequency));
        },
        handleChangeWeekDays: ({ onChange, frequency, _setWeekDays }: OwnProps & _RecomposeProps) => (e: React.ChangeEvent<HTMLInputElement>) => {
            const weekDays = e.target.value === 'WeekDays' && e.target.checked;
            _setWeekDays(weekDays);
            onChange(reportSettings => reportSettings.setScheduleWeekdays(weekDays, parseInt(frequency, 10)));
        },
    }),
    lifecycle({
        componentWillUpdate({ enabled, onChange, weekDays, frequency }: OwnProps & _RecomposeProps) {
            // If this component becomes enabled, need to apply the current frequency value
            // to make sure the XML reflects the current values in the UI.
            if (!this.props.enabled && enabled) {
                onChange(reportSettings => reportSettings.setScheduleWeekdays(weekDays, parseInt(frequency, 10)));
            }
        },
    }),
    withProps(({ frequency, t }: RecomposeProps & TranslateProps) => ({
        sentenceFragments: makeSentenceFragments(t('WC_REPORT_SCHEDULE_DAILY_SENTENCE', { count: parseInt(frequency, 10) })),
    })),
)(RawRecurrenceDaily);



// WEBPACK FOOTER //
// ./src/ui/pages/designer/sections/schedule/runOnSchedule/recurrence/RecurrenceDaily.tsx